import React from "react";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import {
  StyledMainBox,
} from "../../components/commonComponents/StyledComponents/styled";
import { checkUserPermissions, fetchRoles, fetchUsers } from "../../api_calls/userManagement";
import AccessControl from "../../components/commonComponents/Hoc/AccessControl";
import { useDispatch, useSelector } from "react-redux";
import RoleDetail from "./modalContent/RoleDetail";
import { deleteItems } from "../../Store/actions";
import { Box, Link, Stack, Typography } from "@mui/material";
import ListingDataTable from "../../components/commonComponents/TabularLists/ListingDataTable";
import {
  ListingCellLeft,
  ListingCellCenter,
  ListingHeaderContent,
} from "../../components/commonComponents/TabularLists/styles";

const Roles = () => {
  const currentStateData = useSelector((state) => state.userSelections);
  const [selectedRoleInfo, setSelectedRoleInfo] = React.useState({});
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  const dispatchAction = useDispatch();
  const [open, setOpen] = React.useState(false);

  const closeSection = (newOpen) => {
    setOpen(newOpen);
    dispatchAction(deleteItems(["assignable_users_list", "assignable_cyreen_users"]));
  };
  // sorting measures for files listing
  const [sortingMeasure, setSortingMeasure] = React.useState({
    rolename: "asc",
    userCount: "asc",
  });
  const [sortingParamName, setSortingParamName] = React.useState("rolename");

  // to return the sorted list items
  const sortedResults = (toBeSortedList) => {
    return toBeSortedList.slice().sort((a, b) => {
      if (sortingParamName === "rolename") {
        return sortingMeasure.rolename === "asc"
          ? a.role_name.localeCompare(b.role_name)
          : b.role_name.localeCompare(a.role_name);
      } else if (sortingParamName === "userCount") {
        // assigned_users_count
        return sortingMeasure.userCount === "asc"
          ? parseInt(a.assigned_users_count) - parseInt(b.assigned_users_count)
          : parseInt(b.assigned_users_count) - parseInt(a.assigned_users_count);
      } else {
        return sortingMeasure.rolename === "asc"
          ? a.role_name.localeCompare(b.role_name)
          : b.role_name.localeCompare(a.role_name);
      }
    });
  };

  const customerRoles =
    typeof currentStateData.role_list !== "undefined" && currentStateData.role_list.length !== 0
      ? sortedResults(
        currentStateData.role_list.filter((role) => {
          if (!role.role_cyreen === true) {
            if (permissionScope.user_list_can_edit_external === true && role.role_name.toLowerCase() !== "brand") {
              return true;
            }

            if (permissionScope.user_list_can_edit_brand === true && role.role_name.toLowerCase() === "brand") {
              return true;
            }
          } else {
            return false;
          }
        })
      )
      : [];

  const cyreenRoles =
    typeof currentStateData.role_list !== "undefined" && currentStateData.role_list.length !== 0
      ? sortedResults(
        currentStateData.role_list.filter((role) => {
          if (role.role_cyreen === true) {
            if (
              permissionScope.user_list_can_delete_cyreen === true &&
              permissionScope.user_list_can_edit_cyreen === true
            ) {
              return true;
            }
          } else {
            return false;
          }
        })
      )
      : [];

  const handleSortingParamChange = (paramName) => {
    setSortingMeasure((prevState) => {
      return {
        ...prevState,
        [paramName]: prevState[paramName] === "asc" ? "desc" : "asc",
      };
    });
    setSortingParamName(paramName);
  };

  const rows = React.useMemo(() => {
    const result = [];

    if (customerRoles.length !== 0) {
      result.push({
        __rowType: "section",
        id: "customer_roles_section",
        label: "Customer Roles",
      });

      customerRoles.forEach((role) => {
        result.push({
          ...role,
          __rowType: "role",
        });
      });
    }

    if (cyreenRoles.length !== 0) {
      result.push({
        __rowType: "section",
        id: "cyreen_roles_section",
        label: "Cyreen Roles",
      });

      cyreenRoles.forEach((role) => {
        result.push({
          ...role,
          __rowType: "role",
        });
      });
    }

    return result;
  }, [customerRoles, cyreenRoles]);

  const columns = React.useMemo(
    () => [
      {
        id: "name",
        header: (
          <ListingHeaderContent>
            Name
            {sortingMeasure.rolename === "asc" ? (
              <ArrowDropDown
                sx={{ cursor: "pointer" }}
                onClick={() => handleSortingParamChange("rolename")}
              />
            ) : (
              <ArrowDropUp
                sx={{ cursor: "pointer" }}
                onClick={() => handleSortingParamChange("rolename")}
              />
            )}
          </ListingHeaderContent>
        ),
        headerSx: { width: 220, minWidth: 220 },
        cellSx: { width: 220, minWidth: 220 },
        render: (row) => {
          if (row.__rowType === "section") {
            return (
              <ListingCellLeft>
                <Typography variant="body2">
                  <strong>{row.label}</strong>
                </Typography>
              </ListingCellLeft>
            );
          }

          return (
            <ListingCellLeft>
              <Link
                href="#"
                underline="none"
                aria-label={"check details for " + row.role_name}
                title={row.role_name}
                sx={{ color: "#AF3241" }}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedRoleInfo(row);
                  setOpen(true);
                }}
              >
                {row.role_name}
              </Link>
            </ListingCellLeft>
          );
        },
      },

      {
        id: "description",
        header: "Description",
        render: (row) => {
          if (row.__rowType === "section") return null;

          return (
            <ListingCellLeft>
              {row.description}
            </ListingCellLeft>
          );
        },
      },

      {
        id: "modules",
        header: "Modules",
        render: (row) => {
          if (row.__rowType === "section") return null;

          return (
            <ListingCellLeft>
              {row.modules}
            </ListingCellLeft>
          );
        },
      },

      {
        id: "users",
        header: (
          <ListingHeaderContent>
            Users
            {sortingMeasure.userCount === "asc" ? (
              <ArrowDropDown
                sx={{ cursor: "pointer" }}
                onClick={() => handleSortingParamChange("userCount")}
              />
            ) : (
              <ArrowDropUp
                sx={{ cursor: "pointer" }}
                onClick={() => handleSortingParamChange("userCount")}
              />
            )}
          </ListingHeaderContent>
        ),
        headerAlign: "center",
        align: "center",
        headerSx: { width: 100, minWidth: 100 },
        cellSx: { width: 100, minWidth: 100 },
        render: (row) => {
          if (row.__rowType === "section") return null;

          return (
            <ListingCellCenter>
              {row.assigned_users_count}
            </ListingCellCenter>
          );
        },
      },
    ],
    [sortingMeasure]
  );

  React.useEffect(() => {
    fetchRoles(true);
  }, []);

  return (
    <>
      {/* ========== main component body =========== */}
      <StyledMainBox component="main">
        <Box height={30} />
        <h1>Roles</h1>
        <Box height={5} />
        <Typography variant="body2" sx={{ fontSize: "15px" }}>
          This section lists all permission roles that you are able to assign to users. You may review the details of each
          role and assign user(s) to the listed permission roles by clicking on the individual roles.
        </Typography>
        {/* ============ Action buttons =========== */}
        {/* <Stack direction="row" spacing={2}>
          <ActionButton
            onClick={(e) => {
              e.preventDefault();
              openModal();
            }}
            label={"New Role"}
            icon={<Add />}
          />
        </Stack> */}
        {/* ============== roles listing ============ */}
        {/* ============== users listing ============ */}
        <Box height={30} />
        <ListingDataTable
          columns={columns}
          rows={rows}
          rowKey={(row, idx) => row.id ?? row.role_name ?? idx}
          emptyText="No roles found"
          pagination={null}
          onPageChange={null}
          onPageSizeChange={null}
          loading={rows.length === 0 ? true : false}
        />
      </StyledMainBox>
      {/* ============ modal popup for different actions ========== */}
      {open === true && <RoleDetail showSection={open} closeSection={closeSection} roleInfo={selectedRoleInfo} />}
    </>
  );
};

export default AccessControl(Roles, ["user_list_can_view"], [], ["user_section", "role_list"]);
